import { PageController } from '@client/components/_core/layout/PageController';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { NumericInput } from '@client/components/_ui/data-entry/NumericInput';
import { errorMessage } from '@client/components/_ui/feedback/error-message/ErrorMessage';
import { message } from '@client/components/_ui/feedback/message';
import { type RouterInputs, trpc } from '@client/trpc/client';
import { buildNumericInputRules } from '@client/utils/validation/numeric';
import { CLAIM_ID_GENERATOR_DEFAULTS } from '@shared/constants';
import { createFileRoute } from '@tanstack/react-router';
import { Card, Form, Input, Skeleton } from 'antd';
import { useEffect } from 'react';

export const Route = createFileRoute('/console/$orgId/configuration/claims-products/claims/id')({
    component: ClaimIdConfigurationRoute,
});

function ClaimIdConfigurationRoute() {
    return (
        <PageController route={Route}>
            <ClaimIdConfiguration />
        </PageController>
    );
}

type IdGenerator = RouterInputs['idGenerator']['upsertIdGenerator'];

const FormItem = Form.Item<IdGenerator>;

function ClaimIdConfiguration() {
    const { data, isPending } = trpc.idGenerator.getIdGenerator.useQuery({
        entity: CLAIM_ID_GENERATOR_DEFAULTS.entity,
    });
    const { mutate } = trpc.idGenerator.upsertIdGenerator.useMutation();

    const [form] = Form.useForm<IdGenerator>();

    useEffect(() => {
        form.setFieldsValue(
            data
                ? {
                      prefix: data.prefix ?? undefined,
                      template: data.template ?? undefined,
                      zeroPadding: data.zeroPadding ?? undefined,
                      counter: data.counter ?? undefined,
                  }
                : {
                      prefix: CLAIM_ID_GENERATOR_DEFAULTS.prefix,
                      template: CLAIM_ID_GENERATOR_DEFAULTS.template,
                      zeroPadding: CLAIM_ID_GENERATOR_DEFAULTS.zeroPadding,
                      counter: CLAIM_ID_GENERATOR_DEFAULTS.counter,
                  },
        );
    }, [data]);

    return (
        <Card>
            <Form
                form={form}
                labelCol={{ span: 10 }}
                wrapperCol={{ span: 14 }}
                className="max-w-md"
                onFinish={(values) => {
                    message.loading('Saving...');

                    mutate(
                        {
                            entity: 'claim',
                            counter: values.counter ?? CLAIM_ID_GENERATOR_DEFAULTS.counter,
                            prefix: values.prefix ?? CLAIM_ID_GENERATOR_DEFAULTS.prefix,
                            template: values.template ?? CLAIM_ID_GENERATOR_DEFAULTS.template,
                            zeroPadding: values.zeroPadding ?? CLAIM_ID_GENERATOR_DEFAULTS.zeroPadding,
                        },
                        {
                            onSuccess() {
                                message.success('Saved.');
                            },
                            onError(error) {
                                errorMessage.show(error);
                            },
                        },
                    );
                }}
            >
                {isPending ? (
                    <Skeleton active />
                ) : (
                    <>
                        <FormItem label="Prefix" name="prefix">
                            <Input />
                        </FormItem>
                        <FormItem label="Template" name="template" rules={[{ required: true }]}>
                            <Input />
                        </FormItem>
                        <FormItem
                            label="Zero padding"
                            name="zeroPadding"
                            rules={buildNumericInputRules({ valueType: 'number', isRequired: true, min: 0 })}
                        >
                            <NumericInput valueType="number" precision={0} />
                        </FormItem>
                        <FormItem
                            label="Counter"
                            name="counter"
                            rules={buildNumericInputRules({ valueType: 'bigint', isRequired: true })}
                        >
                            <NumericInput valueType="bigint" />
                        </FormItem>
                        <Form.Item wrapperCol={{ offset: 6, span: 18 }} className="pt-4">
                            <Button type="primary" htmlType="submit">
                                Save
                            </Button>
                        </Form.Item>
                    </>
                )}
            </Form>
        </Card>
    );
}
